/**
 * Rip basis allocation — distributes a sealed box's "Basis Removed" cost
 * across the card lots pulled from that rip.
 *
 * Why this exists: when you rip a sealed box, the box's basis (Unit Basis ×
 * Qty Opened) leaves the sealed lot. Without allocation, that cost
 * "orphans" — pulled card lots carry Unit Basis 0, so when they're sold
 * COGS = 0 and the original box cost never offsets any sale's revenue.
 * For tax-tracking purposes the box cost should be capitalized into the
 * cards that came out of it, typically by FMV ratio.
 *
 * Allocation rules (decided with the user 2026-05-19):
 *   • FMV-weighted across singles only — bulk pulls keep flat-rate pool basis
 *   • Auto-recompute on every change (rip save, card edit, card delete,
 *     rip meta edit if Basis Removed changes)
 *   • If all pulled card FMVs are zero, fall back to an even split
 *
 * This module exports a single side-effecting function: call it any time
 * the inputs change and it'll rewrite each pulled card's Unit Basis,
 * Total Basis, and Unrealized G/L.
 */
import { DB } from "./db";

/**
 * Sum of box basis ALREADY booked out via dispositions of a rip's pulls —
 * COGS recorded on sales plus per-unit Basis × Qty on trade outgoing lines.
 * This basis is "spent": it has offset realized proceeds already, so it must
 * not be redistributed onto the cards still held (doing so double-counted the
 * box cost and made profitable boxes read as losses).
 */
export function bookedBasisForRipPulls(ripId: string): number {
  const ids = new Set(
    DB.table("cards").filter(c => c["Source ID"] === ripId).map(c => c["Card Lot ID"])
  );
  if (!ids.size) return 0;
  let total = 0;
  for (const sale of DB.table("sales")) {
    if (String(sale["Status"] || "Completed") !== "Completed") continue;
    const items: any[] = Array.isArray(sale.Items) ? sale.Items : [];
    for (const it of items) {
      if (ids.has(it["Lot ID"])) total += Number(it["COGS"]) || 0;
    }
  }
  for (const t of DB.table("trades")) {
    if (String(t["Status"] || "Accepted") !== "Accepted") continue;
    const out: any[] = Array.isArray(t.Outgoing) ? t.Outgoing : [];
    for (const o of out) {
      if (ids.has(o["Lot ID"])) total += (Number(o["Basis"]) || 0) * (Number(o["Qty"]) || 1);
    }
  }
  return +total.toFixed(2);
}

/**
 * Recompute and write the per-card basis allocation for one rip.
 * Idempotent — safe to call repeatedly. Returns the number of cards updated.
 *
 * Conserving model: the box's Basis Removed is split between basis already
 * realized through dispositions (sale COGS + trade outgoing basis) and the
 * basis still held in inventory. We allocate ONLY the not-yet-realized
 * remainder across the cards still on hand (FMV-weighted) and force fully-sold
 * lots to 0 inventory basis. This guarantees:
 *     (basis booked into sales/trades) + (basis on held cards) === Basis Removed
 * so selling/trading a pull never inflates a sibling's basis.
 */
export function reallocateRipBasis(ripId: string): number {
  if (!ripId) return 0;
  const rip = DB.table("rips").find(r => r["Rip ID"] === ripId);
  if (!rip) return 0;
  const basisRemoved = Number(rip["Basis Removed"]) || 0;

  const pulled = DB.table("cards").filter(c => c["Source ID"] === ripId);
  if (!pulled.length) return 0;

  // Box cost still sitting in inventory = total removed − already booked out.
  const booked = bookedBasisForRipPulls(ripId);
  const remaining = Math.max(0, +(basisRemoved - booked).toFixed(2));

  // Held lots share the remaining basis using FROZEN weights — each pull's
  // FMV at rip time (or at first attachment), stored on the rip. Live FMV is
  // only the fallback for lots that predate the weights map; the self-healed
  // weight is persisted so it never moves again. This keeps every card's
  // basis share fixed even as market prices swing after the rip.
  const held = pulled.filter(c => (Number(c["Qty"]) || 0) > 0);
  const storedWeights: Record<string, number> = (rip["Alloc Weights"] && typeof rip["Alloc Weights"] === "object")
    ? { ...(rip["Alloc Weights"] as Record<string, number>) } : {};
  let weightsChanged = false;
  const weights = held.map(c => {
    const id = String(c["Card Lot ID"]);
    let w = Number(storedWeights[id]);
    if (!Number.isFinite(w)) {
      w = Math.max(0, Number(c["Current FMV"]) || 0);
      storedWeights[id] = w;
      weightsChanged = true;
    }
    return Math.max(0, w);
  });
  if (weightsChanged) DB.update("rips", "Rip ID", ripId, { "Alloc Weights": storedWeights });
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  const evenDiv = held.length;

  let updates = 0;

  // 1) Force every fully-sold lot to zero inventory basis (its share is now
  //    realized in the disposition that emptied it).
  for (const c of pulled) {
    if ((Number(c["Qty"]) || 0) > 0) continue;
    const prevUnit = Number(c["Unit Basis"]) || 0;
    const prevTotal = Number(c["Total Basis"]) || 0;
    if (Math.abs(prevUnit) > 0.005 || Math.abs(prevTotal) > 0.005) {
      DB.update("cards", "Card Lot ID", c["Card Lot ID"], {
        "Unit Basis": 0,
        "Total Basis": 0,
        "Unrealized G/L": +(Number(c["Current FMV"]) || 0).toFixed(2),
      });
      updates++;
    }
  }

  // 2) Spread the remaining basis across the held lots.
  for (let i = 0; i < held.length; i++) {
    const c = held[i];
    const qty = Number(c["Qty"]) || 0;
    let allocatedTotalBasis: number;
    if (totalWeight > 0) {
      allocatedTotalBasis = +((weights[i] / totalWeight) * remaining).toFixed(2);
    } else if (evenDiv > 0) {
      allocatedTotalBasis = +(remaining / evenDiv).toFixed(2);
    } else {
      allocatedTotalBasis = 0;
    }
    const unitBasis = qty > 0 ? +(allocatedTotalBasis / qty).toFixed(2) : 0;
    const currentFMV = Number(c["Current FMV"]) || 0;
    const ugl = +(currentFMV - allocatedTotalBasis).toFixed(2);

    const prevUnit = Number(c["Unit Basis"]) || 0;
    const prevTotal = Number(c["Total Basis"]) || 0;
    if (Math.abs(prevUnit - unitBasis) > 0.005 || Math.abs(prevTotal - allocatedTotalBasis) > 0.005) {
      DB.update("cards", "Card Lot ID", c["Card Lot ID"], {
        "Unit Basis":     unitBasis,
        "Total Basis":    allocatedTotalBasis,
        "Unrealized G/L": ugl,
      });
      updates++;
    }
  }
  return updates;
}

/**
 * One-time heal: reallocate basis across every rip in the database. Used at
 * app load so existing data (rips logged before allocation was implemented)
 * gets corrected the first time the app boots after the update. Idempotent.
 */
export function reallocateAllRipBases(): number {
  let total = 0;
  for (const rip of DB.table("rips")) {
    total += reallocateRipBasis(rip["Rip ID"]);
  }
  return total;
}

/**
 * True if a Source ID string points at an existing rip. Used by saveCard to
 * decide whether to trigger reallocation after a card edit.
 */
export function sourceIdIsRip(sourceId: string | null | undefined): boolean {
  if (!sourceId) return false;
  return DB.table("rips").some(r => r["Rip ID"] === sourceId);
}
